<template>
  <div class="place-selection-shared-map">
    <div ref="geocoderContainer" class="draw-map-geocoder-overlay"></div>

    <!-- Contextual banner overlays (ported from PlaceEditModal .pem-map-banner) -->
    <div
      v-if="bannerState.info"
      class="ps-map-banner ps-map-banner--info"
    >
      Click to add points. Click the first point or double-click to finish.
    </div>
    <transition name="ps-map-banner-fade">
      <div
        v-if="bannerState.warning"
        class="ps-map-banner ps-map-banner--warning"
      >
        Draw a polygon.
      </div>
    </transition>

    <!-- Draw toolbar overlaid on the map (only in draw mode) -->
    <div v-if="mode === 'draw'" class="ps-draw-toolbar">
      <b-button
        variant="light"
        size="sm"
        class="ps-draw-toolbar__btn"
        :disabled="!canUndo"
        title="Undo"
        @click="onUndo"
      >
        <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
      </b-button>
      <b-button
        variant="light"
        size="sm"
        class="ps-draw-toolbar__btn"
        :disabled="!canRedo"
        title="Redo"
        @click="onRedo"
      >
        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
      </b-button>
      <b-button
        v-if="!isDrawing"
        variant="primary"
        size="sm"
        class="ps-draw-toolbar__btn"
        title="Draw polygon"
        @click="onAddPolygon"
      >
        <i class="bi bi-plus-lg mr-1" aria-hidden="true"></i>
        Draw polygon
      </b-button>
      <b-button
        v-if="isDrawing"
        variant="warning"
        size="sm"
        class="ps-draw-toolbar__btn"
        title="Cancel drawing"
        @click="onCancelDrawing"
      >
        <i class="bi bi-x-lg mr-1" aria-hidden="true"></i>
        Cancel
      </b-button>
      <b-button
        v-if="!isDrawing"
        variant="danger"
        size="sm"
        class="ps-draw-toolbar__btn"
        :disabled="!canDelete"
        title="Delete"
        @click="onDeleteDrawing"
      >
        <i class="bi bi-trash" aria-hidden="true"></i>
      </b-button>
    </div>

    <div ref="mapContainer" class="place-selection-shared-map__container"></div>
    <Basemaps ref="mapBasemap" :mapUpdatedCallback="changeMapStyle" />
  </div>
</template>

<script>
import Basemaps from '@/components/partials/Basemaps.vue';
import centerOfMass from '@turf/center-of-mass';
import { createPlaceMarker, removePlaceMarkers } from '@/services/map/place-markers';
import {
  current as historyCurrent,
  canUndo as historyCanUndo,
  canRedo as historyCanRedo,
  recordSnapshot as historyRecordSnapshot,
  reset as historyReset,
  undo as historyUndo,
  redo as historyRedo,
} from './placeSelectionDrawHistory';
import { bannerVisibility, buildDeleteMessage, cloneJSON } from './placeSelectionDrawHelpers';
import { deriveCountryAlpha3 } from './placeSelectionGeocoderHelpers';

const MAX_HISTORY = 50;

const SOURCE_COLORS = {
  library: '#3b82f6',
  draw: '#f59e0b',
  upload: '#10b981',
  csv: '#10b981',
};

const REVIEW_EDIT_HIGHLIGHT = {
  fill: '#4f14dc',
  outline: '#2d0a8d',
};

export default {
  name: 'PlaceSelectionSharedMap',
  components: {
    Basemaps,
  },
  props: {
    mode: {
      type: String,
      default: 'review',
    },
    exploreMapData: {
      type: Object,
      default: null,
    },
    exploreZoomCommand: {
      type: Object,
      default: null,
    },
    uploadPreviewFeatures: {
      type: Array,
      default: () => [],
    },
    drawCommand: {
      type: Object,
      default: null,
    },
    reviewEditPlaceId: {
      type: String,
      default: null,
    },
    reviewEditCommand: {
      type: Object,
      default: null,
    },
    reviewFocusCommand: {
      type: Object,
      default: null,
    },
    activePlaceId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      map: null,
      draw: null,
      geocoder: null,
      geocoderRetrieveHandler: null,
      geocoderCoordinateMarker: null,
      geocoderBoundMap: false,
      lastExploreZoomCommand: null,
      lastExplorePointsSignature: null,
      drawControlAdded: false,
      drawFeaturesSnapshot: [],
      history: [],
      historyIndex: -1,
      isDrawing: false,
      suppressHistory: false,
      drawError: '',
      showPolygonWarning: false,
      polygonWarningTimer: null,
      createJustHappened: false,
      uploadMarkers: [],
      reviewMarkers: [],
      isSatelliteView: false,
      // Debounce timer for the viewport-changed emit (Select tab auto-fills
      // in-view place geometries on zoom). Coalesces rapid pan/zoom so the
      // downstream bulk-geometry resolution only runs once movement settles.
      viewportEmitTimer: null,
      geocodedAddress: '',
      geocodedName: '',
      drawContext: null,
      activeReviewEditPlaceId: null,
      skipNextReviewFit: false,
      currentStyle: 'mapbox://styles/nearco/cku4lf4wi1qn217pq9kh4w3ol',
      viewState: {
        center: [-98, 38.88],
        zoom: 3,
      },
    };
  },
  computed: {
    selectedPlaces() {
      return this.$sessionStore.state.placeSelection.selectedPlaces;
    },
    allFeatures() {
      return this.selectedPlaces
        .filter((p) => p.geometry)
        .map((p) => ({
          type: 'Feature',
          properties: {
            name: p.name,
            _source: p._source || 'library',
          },
          geometry: p.geometry,
        }));
    },
    currentSnapshot() {
      return historyCurrent({ history: this.history, index: this.historyIndex });
    },
    canUndo() {
      return historyCanUndo({ history: this.history, index: this.historyIndex });
    },
    canRedo() {
      return historyCanRedo({ history: this.history, index: this.historyIndex });
    },
    canDelete() {
      return this.drawFeaturesSnapshot.length > 0;
    },
    bannerState() {
      // Contextual banners are only meaningful while the draw flow is active.
      if (this.mode !== 'draw') {
        return { info: false, warning: false };
      }
      // The info banner still tracks Drawing_Mode. The warning is no longer
      // shown persistently whenever there are no shapes; instead it is a
      // transient prompt surfaced only when the user attempts to add a place
      // without a polygon (see showDrawWarning), and it fades on its own.
      const { info } = bannerVisibility(this.isDrawing, this.drawFeaturesSnapshot.length);
      return { info, warning: this.showPolygonWarning };
    },
  },
  watch: {
    mode(nextMode, prevMode) {
      if (prevMode === 'review-edit' && nextMode === 'review') {
        this.skipNextReviewFit = true;
      }
      // Re-entering select mode should fit to the current results again.
      if (nextMode === 'select' && prevMode !== 'select') {
        this.lastExplorePointsSignature = null;
      }
      this.applyMode();
    },
    exploreMapData: {
      deep: true,
      handler() {
        // If the explore layers/source already exist, update them in place so a
        // late-arriving polygon still renders even if the map's mode has since
        // transitioned away from 'select' (e.g. into review). Otherwise fall
        // back to the mode-gated setup path.
        if (this.map && this.map.getSource('ps-explore-polygons')) {
          this.updateExploreData();
        } else if (this.mode === 'select') {
          this.applyMode();
        }
      },
    },
    exploreZoomCommand: {
      deep: true,
      handler() {
        this.handleExploreZoomCommand();
      },
    },
    selectedPlaces: {
      deep: true,
      handler() {
        this.applyMode();
      },
    },
    uploadPreviewFeatures: {
      deep: true,
      handler() {
        if (this.mode === 'upload') {
          this.applyMode();
        }
      },
    },
    drawCommand: {
      deep: true,
      handler() {
        this.handleDrawCommand();
      },
    },
    reviewEditCommand: {
      deep: true,
      handler() {
        this.handleReviewEditCommand();
      },
    },
    reviewFocusCommand: {
      deep: true,
      handler() {
        this.handleReviewFocusCommand();
      },
    },
    activePlaceId() {
      // When the builder provides an activePlaceId, the review polygons are
      // fed only the active place's feature. Update the polygon sources in
      // place and toggle the active-marker highlight WITHOUT re-rendering the
      // markers (which would recreate the DOM elements). Existing callers that
      // never set the prop keep rendering all polygons.
      if (this.mode === 'review') {
        this.updateReviewPolygons();
      }
      this.applyActiveMarkerClass();
    },
  },
  mounted() {
    this.$nextTick(() => this.initMap());
  },
  beforeDestroy() {
    if (this.polygonWarningTimer) {
      clearTimeout(this.polygonWarningTimer);
      this.polygonWarningTimer = null;
    }
    if (this.viewportEmitTimer) {
      clearTimeout(this.viewportEmitTimer);
      this.viewportEmitTimer = null;
    }
    this.destroyMap();
  },
  methods: {
    initMap() {
      if (this.map || !this.$refs.mapContainer) return;

      console.log(this.$refs.mapBasemap);
      let basemapData = this.$refs.mapBasemap.getMetadata();
      if (basemapData?.mapID) {
        this.currentStyle = `mapbox://styles/nearco/${basemapData.mapID}`;
      }

      mapboxgl.accessToken = process.env.VUE_APP_MAP_ACCESS_TOKEN;
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: this.currentStyle,
        center: this.viewState.center,
        zoom: this.viewState.zoom,
      });

      this.map.addControl(new mapboxgl.NavigationControl(), 'top-left');

      this.map.on('moveend', () => {
        if (!this.map) return;
        const center = this.map.getCenter();
        this.viewState = {
          center: [center.lng, center.lat],
          zoom: this.map.getZoom(),
        };
        this.scheduleViewportEmit();
      });

      this.map.on('load', () => {
        this.applyMode();
      });
    },
    // Debounce (~300 ms) the viewport-changed emit so a burst of moveend events
    // during an interactive pan/zoom coalesces into a single downstream
    // resolution. Only meaningful while exploring (select mode); other modes
    // don't auto-fill geometries.
    scheduleViewportEmit() {
      if (this.mode !== 'select') return;
      if (this.viewportEmitTimer) clearTimeout(this.viewportEmitTimer);
      this.viewportEmitTimer = setTimeout(() => {
        this.viewportEmitTimer = null;
        this.emitViewport();
      }, 300);
    },
    // Report the current map bounds + zoom to the parent so the Select tab can
    // auto-resolve polygon geometries for in-view places (draw-only).
    emitViewport() {
      if (!this.map || this.mode !== 'select') return;
      const bounds = this.map.getBounds();
      if (!bounds) return;
      this.$emit('viewport-changed', {
        bounds: {
          west: bounds.getWest(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          north: bounds.getNorth(),
        },
        zoom: this.map.getZoom(),
      });
    },
    destroyMap() {
      this.clearDrawTools();
      this.clearReviewMarkers();
      this.clearUploadMarkers();
      this.clearGeocoder();
      if (!this.map) return;
      this.map.remove();
      this.map = null;
    },
    ensureReviewLayers() {
      if (!this.map) return;

      for (const [source, color] of Object.entries(SOURCE_COLORS)) {
        const sourceId = `ps-shared-${source}`;

        if (!this.map.getSource(sourceId)) {
          this.map.addSource(sourceId, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          });
        }

        if (!this.map.getLayer(`ps-shared-fill-${source}`)) {
          this.map.addLayer({
            id: `ps-shared-fill-${source}`,
            type: 'fill',
            source: sourceId,
            paint: {
              'fill-color': color,
              'fill-opacity': 0.3,
            },
          });
        }

        if (!this.map.getLayer(`ps-shared-outline-${source}`)) {
          this.map.addLayer({
            id: `ps-shared-outline-${source}`,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': color,
              'line-width': 2,
            },
          });
        }
      }
    },
    clearReviewLayers() {
      if (!this.map) return;

      for (const source of Object.keys(SOURCE_COLORS)) {
        const fillId = `ps-shared-fill-${source}`;
        const outlineId = `ps-shared-outline-${source}`;
        const sourceId = `ps-shared-${source}`;

        if (this.map.getLayer(fillId)) this.map.removeLayer(fillId);
        if (this.map.getLayer(outlineId)) this.map.removeLayer(outlineId);
        if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
      }
    },
    ensureReviewEditHighlightLayers() {
      if (!this.map) return;

      const sourceId = 'ps-shared-review-edit-highlight';
      if (!this.map.getSource(sourceId)) {
        this.map.addSource(sourceId, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }

      if (!this.map.getLayer('ps-shared-review-edit-fill')) {
        this.map.addLayer({
          id: 'ps-shared-review-edit-fill',
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': REVIEW_EDIT_HIGHLIGHT.fill,
            'fill-opacity': 0.18,
          },
        });
      }

      if (!this.map.getLayer('ps-shared-review-edit-outline')) {
        this.map.addLayer({
          id: 'ps-shared-review-edit-outline',
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': REVIEW_EDIT_HIGHLIGHT.outline,
            'line-width': 4,
          },
        });
      }
    },
    clearReviewEditHighlightLayers() {
      if (!this.map) return;

      if (this.map.getLayer('ps-shared-review-edit-outline')) this.map.removeLayer('ps-shared-review-edit-outline');
      if (this.map.getLayer('ps-shared-review-edit-fill')) this.map.removeLayer('ps-shared-review-edit-fill');
      if (this.map.getSource('ps-shared-review-edit-highlight')) this.map.removeSource('ps-shared-review-edit-highlight');
    },
    updateReviewEditHighlightData() {
      if (!this.map || this.mode !== 'review-edit') return;

      const place = this.getReviewEditPlace();
      const src = this.map.getSource('ps-shared-review-edit-highlight');
      if (!src) return;

      if (!place || !place.geometry) {
        src.setData({ type: 'FeatureCollection', features: [] });
        return;
      }

      src.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { _id: place._id, name: place.name || 'Edited place' },
          geometry: place.geometry,
        }],
      });
    },
    clearReviewMarkers() {
      this.reviewMarkers = removePlaceMarkers(this.reviewMarkers);
    },
    ensureUploadLayers() {
      if (!this.map) return;

      const sourceId = 'ps-shared-upload-preview';
      if (!this.map.getSource(sourceId)) {
        this.map.addSource(sourceId, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }

      if (!this.map.getLayer('ps-shared-upload-fill')) {
        this.map.addLayer({
          id: 'ps-shared-upload-fill',
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': '#10b981',
            'fill-opacity': 0.3,
          },
        });
      }

      if (!this.map.getLayer('ps-shared-upload-outline')) {
        this.map.addLayer({
          id: 'ps-shared-upload-outline',
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#059669',
            'line-width': 1.5,
          },
        });
      }
    },
    clearUploadLayers() {
      if (!this.map) return;

      this.clearUploadMarkers();
      if (this.map.getLayer('ps-shared-upload-fill')) this.map.removeLayer('ps-shared-upload-fill');
      if (this.map.getLayer('ps-shared-upload-outline')) this.map.removeLayer('ps-shared-upload-outline');
      if (this.map.getSource('ps-shared-upload-preview')) this.map.removeSource('ps-shared-upload-preview');
    },
    clearUploadMarkers() {
      this.uploadMarkers = removePlaceMarkers(this.uploadMarkers);
    },
    getPlaceMarkerPoint(place) {
      if (!place) return null;

      if (Array.isArray(place.centerLngLat) && place.centerLngLat.length === 2) {
        return place.centerLngLat;
      }

      if (Array.isArray(place.properties?.centerLngLat) && place.properties.centerLngLat.length === 2) {
        return place.properties.centerLngLat;
      }

      const geometry = place.geometry || null;
      if (!geometry) return null;

      if (geometry.type === 'Point' && Array.isArray(geometry.coordinates) && geometry.coordinates.length === 2) {
        return geometry.coordinates;
      }

      try {
        const centerFeature = centerOfMass({ type: 'Feature', geometry, properties: {} });
        const coords = centerFeature?.geometry?.coordinates;
        if (Array.isArray(coords) && coords.length === 2) {
          return coords;
        }
      } catch (error) {
        console.warn('Failed to calculate place center-of-mass', error);
      }

      const coords = this.extractCoords(geometry);
      if (coords.length > 0) {
        return coords[0];
      }

      return null;
    },
    renderUploadMarkers() {
      this.clearUploadMarkers();

      if (!this.map || this.mode !== 'upload') return;
      if (!Array.isArray(this.uploadPreviewFeatures) || !this.uploadPreviewFeatures.length) return;

      this.uploadMarkers = this.uploadPreviewFeatures
        .map((feature, index) => {
          const coordinates = this.getPlaceMarkerPoint(feature);
          if (!coordinates) return null;

          const featureName = feature?.properties?.name ? String(feature.properties.name) : `Feature ${index + 1}`;
          const ariaLabel = `Center marker for ${featureName}`;

          const marker = createPlaceMarker({
            map: this.map,
            center: coordinates,
            label: featureName,
            ariaLabel,
          });

          return marker;
        })
        .filter(Boolean);
    },
    renderReviewMarkers() {
      this.clearReviewMarkers();

      if (!this.map || this.mode !== 'review') return;
      if (!Array.isArray(this.selectedPlaces) || !this.selectedPlaces.length) return;

      this.reviewMarkers = this.selectedPlaces
        .flatMap((place, index) => {
          if (!place) return [];

          // CSV entries carry no top-level geometry; their geometry lives in
          // previewFeatures (an array of GeoJSON Point features, already capped
          // at Preview_Cap). Emit one marker per preview feature.
          if (place._source === 'csv') {
            if (!Array.isArray(place.previewFeatures) || !place.previewFeatures.length) return [];

            return place.previewFeatures
              .map((feature, featureIndex) => {
                const coordinates = this.getPlaceMarkerPoint(feature);
                if (!coordinates) return null;

                const featureName = feature?.properties?.name
                  ? String(feature.properties.name)
                  : `${place.name || `Place ${index + 1}`} ${featureIndex + 1}`;
                const ariaLabel = `Center marker for ${featureName}`;

                const marker = createPlaceMarker({
                  map: this.map,
                  center: coordinates,
                  label: featureName,
                  ariaLabel,
                });

                return this.bindReviewMarker(marker, place._id);
              })
              .filter(Boolean);
          }

          if (!place.geometry) return [];

          const coordinates = this.getPlaceMarkerPoint(place);
          if (!coordinates) return [];

          const placeName = place.name ? String(place.name) : `Place ${index + 1}`;
          const ariaLabel = `Center marker for ${placeName}`;

          const marker = createPlaceMarker({
            map: this.map,
            center: coordinates,
            label: placeName,
            ariaLabel,
          });

          return [this.bindReviewMarker(marker, place._id)];
        })
        .filter(Boolean);

      this.applyActiveMarkerClass();
    },
    bindReviewMarker(marker, placeId) {
      // Associate a review marker with its place so clicks can emit the id and
      // the active-marker highlight can target the right element. Returns the
      // marker (or null) so callers can filter out failures.
      if (!marker || typeof marker.getElement !== 'function') return marker;

      const element = marker.getElement();
      if (!element) return marker;

      element.dataset.placeId = placeId != null ? String(placeId) : '';
      // Markers default to pointer-events: none; enable clicks in review mode.
      element.style.pointerEvents = 'auto';
      element.style.cursor = 'pointer';
      element.addEventListener('click', (event) => {
        event.stopPropagation();
        this.$emit('marker-click', { id: placeId });
      });

      return marker;
    },
    applyActiveMarkerClass() {
      // Toggle the is-active class on review marker elements without a full
      // re-render so the active place's marker stays visually highlighted.
      if (!Array.isArray(this.reviewMarkers)) return;

      const activeId = this.activePlaceId != null ? String(this.activePlaceId) : null;

      this.reviewMarkers.forEach((marker) => {
        if (!marker || typeof marker.getElement !== 'function') return;
        const element = marker.getElement();
        if (!element) return;

        const isActive = activeId !== null && element.dataset.placeId === activeId;
        element.classList.toggle('is-active', isActive);
      });
    },
    ensureExploreLayers() {
      if (!this.map) return;

      const points = this.exploreMapData && this.exploreMapData.pointsGeoJSON
        ? this.exploreMapData.pointsGeoJSON
        : { type: 'FeatureCollection', features: [] };
      const polygons = this.exploreMapData && this.exploreMapData.polygonsGeoJSON
        ? this.exploreMapData.polygonsGeoJSON
        : { type: 'FeatureCollection', features: [] };

      if (!this.map.getSource('ps-explore-points')) {
        this.map.addSource('ps-explore-points', {
          type: 'geojson',
          data: points,
          promoteId: 'placematrix_id',
        });
      }

      if (!this.map.getSource('ps-explore-polygons')) {
        this.map.addSource('ps-explore-polygons', {
          type: 'geojson',
          data: polygons,
        });
      }

      if (!this.map.getLayer('ps-explore-polygons-layer')) {
        this.map.addLayer({
          id: 'ps-explore-polygons-layer',
          type: 'fill',
          source: 'ps-explore-polygons',
          paint: {
            // Azira purple tint so polygons stand out against the basemap.
            'fill-color': '#4F14DC',
            'fill-opacity': 0.25,
          },
        });
      }

      // Dedicated line layer for a darker, thicker border than the 1px
      // hairline fill-outline-color produces, so polygons read clearly.
      if (!this.map.getLayer('ps-explore-polygons-outline')) {
        this.map.addLayer({
          id: 'ps-explore-polygons-outline',
          type: 'line',
          source: 'ps-explore-polygons',
          paint: {
            'line-color': '#2A0A75',
            'line-width': 2,
          },
        });
      }

      if (!this.map.getLayer('ps-explore-points-circle')) {
        this.map.addLayer({
          id: 'ps-explore-points-circle',
          type: 'circle',
          source: 'ps-explore-points',
          paint: {
            'circle-radius': 8,
            'circle-color': [
              'case',
              ['boolean', ['feature-state', 'checked'], false],
              '#4F14DC',
              '#8C8CA1',
            ],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });
      }

      if (!this.map.getLayer('ps-explore-points-checkmark')) {
        this.map.addLayer({
          id: 'ps-explore-points-checkmark',
          type: 'symbol',
          source: 'ps-explore-points',
          layout: {
            'text-field': '✓',
            'text-size': 12,
            'text-font': ['Inter', 'Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-offset': [0, 0],
          },
          paint: {
            'text-color': '#ffffff',
            'text-opacity': [
              'case',
              ['boolean', ['feature-state', 'checked'], false],
              1,
              0,
            ],
          },
        });
      }

      if (!this.map.getLayer('ps-explore-points-label')) {
        this.map.addLayer({
          id: 'ps-explore-points-label',
          type: 'symbol',
          source: 'ps-explore-points',
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Inter', 'Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-offset': [0, -0.8],
            'text-anchor': 'bottom',
          },
          paint: {
            'text-color': '#000',
            'text-halo-color': '#fff',
            'text-halo-width': 1,
          },
        });
      }

      this.map.off('click', 'ps-explore-points-circle', this.onExplorePointClick);
      this.map.on('click', 'ps-explore-points-circle', this.onExplorePointClick);

      this.map.off('mouseenter', 'ps-explore-points-circle', this.onExplorePointMouseEnter);
      this.map.on('mouseenter', 'ps-explore-points-circle', this.onExplorePointMouseEnter);

      this.map.off('mouseleave', 'ps-explore-points-circle', this.onExplorePointMouseLeave);
      this.map.on('mouseleave', 'ps-explore-points-circle', this.onExplorePointMouseLeave);
    },
    clearExploreLayers() {
      if (!this.map) return;

      this.map.off('click', 'ps-explore-points-circle', this.onExplorePointClick);
      this.map.off('mouseenter', 'ps-explore-points-circle', this.onExplorePointMouseEnter);
      this.map.off('mouseleave', 'ps-explore-points-circle', this.onExplorePointMouseLeave);

      if (this.map.getLayer('ps-explore-polygons-outline')) this.map.removeLayer('ps-explore-polygons-outline');
      if (this.map.getLayer('ps-explore-polygons-layer')) this.map.removeLayer('ps-explore-polygons-layer');
      if (this.map.getLayer('ps-explore-points-label')) this.map.removeLayer('ps-explore-points-label');
      if (this.map.getLayer('ps-explore-points-checkmark')) this.map.removeLayer('ps-explore-points-checkmark');
      if (this.map.getLayer('ps-explore-points-circle')) this.map.removeLayer('ps-explore-points-circle');
      if (this.map.getSource('ps-explore-polygons')) this.map.removeSource('ps-explore-polygons');
      if (this.map.getSource('ps-explore-points')) this.map.removeSource('ps-explore-points');
    },
    updateExploreData() {
      if (!this.map) return;

      const points = this.exploreMapData && this.exploreMapData.pointsGeoJSON
        ? this.exploreMapData.pointsGeoJSON
        : { type: 'FeatureCollection', features: [] };
      const polygons = this.exploreMapData && this.exploreMapData.polygonsGeoJSON
        ? this.exploreMapData.polygonsGeoJSON
        : { type: 'FeatureCollection', features: [] };
      const selectedIds = this.exploreMapData && Array.isArray(this.exploreMapData.selectedPlacematrixIds)
        ? this.exploreMapData.selectedPlacematrixIds
        : [];

      const pointsSource = this.map.getSource('ps-explore-points');
      if (pointsSource) {
        pointsSource.setData(points);
      }

      const polygonSource = this.map.getSource('ps-explore-polygons');
      if (polygonSource) {
        polygonSource.setData(polygons);
      }

      const selectedSet = new Set(selectedIds);
      (points.features || []).forEach((feature) => {
        const id = feature && feature.properties ? feature.properties.placematrix_id : null;
        if (!id) return;
        this.map.setFeatureState(
          { source: 'ps-explore-points', id },
          { checked: selectedSet.has(id) },
        );
      });

      // Only fit the map to the points when the point set itself changes
      // (i.e. new search results were fetched). Selecting/deselecting a place
      // or adding a polygon re-runs this update but must not zoom the map out.
      const pointsSignature = (points.features || [])
        .map((feature) => (feature && feature.properties ? feature.properties.placematrix_id : ''))
        .sort()
        .join(',');

      if (pointsSignature !== this.lastExplorePointsSignature) {
        this.lastExplorePointsSignature = pointsSignature;

        if ((points.features || []).length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          points.features.forEach((feature) => {
            const coord = feature && feature.geometry ? feature.geometry.coordinates : null;
            if (Array.isArray(coord) && coord.length === 2) {
              bounds.extend(coord);
            }
          });
          if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds, { padding: 40, maxZoom: 13 });
          }
        }
      }
    },
    onExplorePointClick(e) {
      const clicked = e && Array.isArray(e.features) ? e.features[0] : null;
      const id = clicked && clicked.properties ? clicked.properties.placematrix_id : null;
      if (!id) return;
      this.$emit('explore-point-clicked', id);
    },
    onExplorePointMouseEnter() {
      if (!this.map) return;
      this.map.getCanvas().style.cursor = 'pointer';
    },
    onExplorePointMouseLeave() {
      if (!this.map) return;
      this.map.getCanvas().style.cursor = '';
    },
    handleExploreZoomCommand() {
      if (!this.map || this.mode !== 'select' || !this.exploreZoomCommand || !Array.isArray(this.exploreZoomCommand.center)) return;

      // Only zoom when a new command arrives (i.e. the user clicked the zoom
      // button). Marker/checkbox interactions re-run explore data updates but
      // must not re-trigger a zoom to the last place.
      if (this.exploreZoomCommand === this.lastExploreZoomCommand) return;
      this.lastExploreZoomCommand = this.exploreZoomCommand;

      const zoom = typeof this.exploreZoomCommand.zoom === 'number'
        ? this.exploreZoomCommand.zoom
        : this.map.getZoom();

      this.map.jumpTo({
        center: this.exploreZoomCommand.center,
        zoom,
      });
    },
    ensureDrawTools(context = 'draw') {
      if (!this.map) return;

      if (!this.draw) {
        this.draw = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            //polygon: true,
            //trash: true,
          },
        });
      }

      if (!this.drawControlAdded) {
        this.map.addControl(this.draw, 'top-left');
        this.drawControlAdded = true;
      }

      this.drawContext = context;

      // Detach any previously attached draw listeners (both the legacy
      // onDrawUpdate bindings and the history-aware draw-context bindings) so a
      // context switch never leaves double-fired handlers.
      this.map.off('draw.create', this.onDrawUpdate);
      this.map.off('draw.update', this.onDrawUpdate);
      this.map.off('draw.delete', this.onDrawUpdate);
      this.map.off('draw.create', this.onDrawCreate);
      this.map.off('draw.update', this.onDrawUpdateOrDelete);
      this.map.off('draw.delete', this.onDrawUpdateOrDelete);
      this.map.off('draw.modechange', this.onDrawModeChange);

      if (context === 'draw') {
        // Draw context owns the history + drawing-mode toolbar behavior.
        this.map.on('draw.modechange', this.onDrawModeChange);
        this.map.on('draw.create', this.onDrawCreate);
        this.map.on('draw.update', this.onDrawUpdateOrDelete);
        this.map.on('draw.delete', this.onDrawUpdateOrDelete);
        // Double-click zoom conflicts with double-click-to-finish-polygon.
        if (this.map.doubleClickZoom) {
          this.map.doubleClickZoom.disable();
        }
      } else {
        // review-edit (and any other context) keeps the original behavior:
        // create/update/delete all funnel through onDrawUpdate.
        this.map.on('draw.create', this.onDrawUpdate);
        this.map.on('draw.update', this.onDrawUpdate);
        this.map.on('draw.delete', this.onDrawUpdate);
      }

      if (
        context === 'draw'
        && Array.isArray(this.drawFeaturesSnapshot)
        && this.drawFeaturesSnapshot.length > 0
      ) {
        this.draw.set({
          type: 'FeatureCollection',
          features: this.drawFeaturesSnapshot,
        });
      }
      this.emitDrawState();
    },
    clearDrawTools() {
      if (!this.map) return;

      if (this.draw && this.drawControlAdded && this.drawContext === 'draw') {
        try {
          const data = this.draw.getAll();
          this.drawFeaturesSnapshot = data && Array.isArray(data.features) ? data.features : [];
        } catch (error) {
          this.drawFeaturesSnapshot = [];
        }
      }

      this.map.off('draw.create', this.onDrawUpdate);
      this.map.off('draw.update', this.onDrawUpdate);
      this.map.off('draw.delete', this.onDrawUpdate);
      this.map.off('draw.create', this.onDrawCreate);
      this.map.off('draw.update', this.onDrawUpdateOrDelete);
      this.map.off('draw.delete', this.onDrawUpdateOrDelete);
      this.map.off('draw.modechange', this.onDrawModeChange);

      // The draw context disables doubleClickZoom; restore it on teardown.
      if (this.drawContext === 'draw' && this.map.doubleClickZoom) {
        this.map.doubleClickZoom.enable();
      }

      if (this.draw && this.drawControlAdded) {
        this.map.removeControl(this.draw);
        this.drawControlAdded = false;
        // Drop the removed MapboxDraw instance so ensureDrawTools() builds a
        // fresh one next time. A draw control that has been through
        // removeControl -> addControl ends up in a broken internal state where
        // changeMode('draw_polygon') silently no-ops (which broke the draw
        // button after a basemap swap). The drawn features are preserved in
        // this.drawFeaturesSnapshot above and restored by ensureDrawTools().
        this.draw = null;
      }

      this.drawContext = null;
      this.activeReviewEditPlaceId = null;
    },
    clearGeocoder() {
      if (this.geocoder) {
        if (this.geocoderRetrieveHandler) {
          this.geocoder.removeEventListener('retrieve', this.geocoderRetrieveHandler);
        }
        if (this.geocoderBoundMap && typeof this.geocoder.unbindMap === 'function') {
          this.geocoder.unbindMap();
        }
      }
      this.removeCoordinateMarker();
      this.geocoderRetrieveHandler = null;
      this.geocoderBoundMap = false;
      this.geocoder = null;

      if (!this.$refs.geocoderContainer) return;
      this.$refs.geocoderContainer.innerHTML = '';
    },
    parseCoordinateString(text) {
      if (typeof text !== 'string') return null;

      const match = text.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
      if (!match) return null;

      const a = parseFloat(match[1]);
      const b = parseFloat(match[2]);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

      let lng;
      let lat;
      if (Math.abs(a) > 90 && Math.abs(b) <= 90) {
        // First value is out of latitude range, so it must be longitude (lng,lat).
        lng = a;
        lat = b;
      } else if (Math.abs(b) > 90 && Math.abs(a) <= 90) {
        // Second value is out of latitude range, so input was (lat,lng).
        lat = a;
        lng = b;
      } else {
        // Ambiguous (both within ±90): assume lng,lat per Mapbox convention.
        lng = a;
        lat = b;
      }

      if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return null;
      return [lng, lat];
    },
    removeCoordinateMarker() {
      if (this.geocoderCoordinateMarker) {
        this.geocoderCoordinateMarker.remove();
        this.geocoderCoordinateMarker = null;
      }
    },
    handleCoordinateSearch([lng, lat]) {
      if (!this.map) return;

      this.geocodedAddress = `${lat}, ${lng}`;
      this.geocodedName = '';
      this.removeCoordinateMarker();
      this.geocoderCoordinateMarker = new mapboxgl.Marker().setLngLat([lng, lat]).addTo(this.map);
      this.map.flyTo({
        center: [lng, lat],
        zoom: 16,
        essential: true,
        duration: 900,
      });
      this.emitDrawState();
    },
    extractSearchFeature(detail) {
      if (!detail) return null;
      if (Array.isArray(detail.features)) return detail.features[0] || null;
      return detail;
    },
    // Additive to the retrieve handler: only in Select_Mode, and only when a
    // country is derivable, report the geocoded country up to the parent. The
    // camera path (pan/zoom) is unchanged and runs regardless of this emit.
    // (Req 2.1, 6.1, 6.2, 6.3)
    emitGeocodedCountry(result) {
      if (this.mode !== 'select') return;
      const alpha3 = deriveCountryAlpha3(result);
      if (alpha3) this.$emit('geocoded-country', alpha3);
    },
    getSearchResultLabel(result = {}) {
      if (result.full_address) return String(result.full_address);
      if (result.place_formatted) return String(result.place_formatted);
      if (result.name) return String(result.name);
      if (result.properties && result.properties.full_address) return String(result.properties.full_address);
      if (result.properties && result.properties.place_formatted) return String(result.properties.place_formatted);
      if (result.properties && result.properties.name) return String(result.properties.name);
      return '';
    },
    // Return the POI's own name (not its address) so the Draw tab can prefill
    // the Place Name field. Only POI-type results carry a meaningful name;
    // plain addresses/streets/regions do not, so we skip those.
    getSearchResultName(result = {}) {
      const featureType = result.feature_type
        || (result.properties && result.properties.feature_type)
        || '';
      if (String(featureType).toLowerCase() !== 'poi') return '';

      if (result.name) return String(result.name);
      if (result.properties && result.properties.name) return String(result.properties.name);
      return '';
    },
    getSearchResultBbox(result = {}) {
      if (Array.isArray(result.bbox) && result.bbox.length === 4) {
        return result.bbox;
      }

      if (result.properties && Array.isArray(result.properties.bbox) && result.properties.bbox.length === 4) {
        return result.properties.bbox;
      }

      return null;
    },
    getSearchResultCenter(result = {}) {
      if (Array.isArray(result.center) && result.center.length === 2) {
        return result.center;
      }

      if (
        result.geometry
        && Array.isArray(result.geometry.coordinates)
        && result.geometry.coordinates.length === 2
      ) {
        return result.geometry.coordinates;
      }

      if (
        result.properties
        && result.properties.coordinates
        && typeof result.properties.coordinates.longitude === 'number'
        && typeof result.properties.coordinates.latitude === 'number'
      ) {
        return [result.properties.coordinates.longitude, result.properties.coordinates.latitude];
      }

      return null;
    },
    mountGeocoder() {
      if (!this.map || typeof MapboxSearchBox !== 'function' || !this.$refs.geocoderContainer) return;

      this.clearGeocoder();

      this.geocoder = new MapboxSearchBox();
      this.geocoder.accessToken = mapboxgl.accessToken;
      this.geocoder.mapboxgl = mapboxgl;
      this.geocoder.marker = true;
      this.geocoder.placeholder = 'Search addresses or POIs to pan the map';
      this.geocoder.options = {
        limit: 10,
        types: 'poi,address,street,neighborhood,locality,place,postcode,region,country,category',
      };

      // Let users paste/type raw coordinates (e.g. "-122.273028,47.693188")
      // and jump straight there. interceptSearch runs before any suggest API
      // call, so returning '' short-circuits the request and avoids a 400.
      this.geocoder.interceptSearch = (value) => {
        const coords = this.parseCoordinateString(value);
        if (coords) {
          this.handleCoordinateSearch(coords);
          return '';
        }
        return value;
      };
      this.geocoderRetrieveHandler = (evt) => {
        const detail = evt && evt.detail ? evt.detail : null;
        const result = this.extractSearchFeature(detail);
        if (!result || !this.map) return;

        // A standard search result supersedes any coordinate-search marker.
        this.removeCoordinateMarker();
        this.geocodedAddress = this.getSearchResultLabel(result);
        this.geocodedName = this.getSearchResultName(result);

        // Only drive the camera manually when the control isn't map-bound,
        // otherwise the built-in flyTo would double-animate.
        if (!this.geocoderBoundMap) {
          const bbox = this.getSearchResultBbox(result);
          if (bbox) {
            this.map.fitBounds(bbox, { padding: 50, duration: 900 });
            this.emitGeocodedCountry(result);
            this.emitDrawState();
            return;
          }

          const center = this.getSearchResultCenter(result);
          if (center) {
            this.map.flyTo({ center, zoom: 16, essential: true, duration: 900 });
          }
        }

        this.emitGeocodedCountry(result);
        this.emitDrawState();
      };

      this.geocoder.addEventListener('retrieve', this.geocoderRetrieveHandler);
      this.$refs.geocoderContainer.appendChild(this.geocoder);

      // Bind to the map so the built-in marker + flyTo work while keeping
      // the control in our custom overlay container.
      if (typeof this.geocoder.bindMap === 'function') {
        this.geocoder.bindMap(this.map);
        this.geocoderBoundMap = true;
      }
    },
    onDrawUpdate() {
      if (!this.draw || !this.drawControlAdded) return;

      if (this.mode === 'review-edit') {
        return;
      }

      if (this.mode !== 'draw') {
        return;
      }

      let data;
      try {
        data = this.draw.getAll();
      } catch (error) {
        return;
      }

      this.drawFeaturesSnapshot = data && Array.isArray(data.features) ? data.features : [];
      this.emitDrawState();
    },
    emitDrawState() {
      this.$emit('draw-state-changed', {
        drawnShapes: this.drawFeaturesSnapshot,
        isSatelliteView: this.isSatelliteView,
        geocodedAddress: this.geocodedAddress,
        geocodedName: this.geocodedName,
        isDrawing: this.isDrawing,
        canUndo: this.canUndo,
        canRedo: this.canRedo,
        canDelete: this.canDelete,
        drawError: this.drawError,
      });
    },
    changeMapStyle(evt) {
      if (!this.map || !evt || !evt.mapID) return;

      const nextStyle = `mapbox://styles/nearco/${evt.mapID}`;
      this.currentStyle = nextStyle;
      this.isSatelliteView = evt.baseMap === 'satellite';

      // Keep the single persistent mapbox-gl-draw control registered across the
      // style swap. Tearing it down with clearDrawTools() before setStyle() and
      // rebuilding it afterwards is the mapbox-gl-draw remove->add cycle (#764)
      // that left the canvas with no crosshair and no click capture, so the
      // "Draw polygon" button no-opped after a basemap change. While the control
      // stays registered, Draw re-injects its own sources/layers on styledata
      // and the crosshair + click handling survive the style swap. Drawn shapes
      // remain live in the Draw instance (and continue to be mirrored into
      // drawFeaturesSnapshot by onDrawUpdate), so nothing needs snapshotting
      // here and no history re-seed / camera re-fit is introduced.

      // Capture the current camera BEFORE the style swap. The forced re-add
      // below runs the mode's data update, whose review/select/upload branches
      // re-fit the camera (fitBounds/fitFeatures). On a basemap change the user
      // had already framed the view, so restoring the pre-swap camera keeps the
      // basemap change from altering zoom/center/bearing/pitch.
      const camera = {
        center: this.map.getCenter(),
        zoom: this.map.getZoom(),
        bearing: this.map.getBearing(),
        pitch: this.map.getPitch(),
      };
      this.map.setStyle(this.currentStyle);
      this.map.once('style.load', () => {
        // Force the re-add so Review/Select/Upload (and review-edit) restore
        // their sources+layers even when isStyleLoaded() momentarily reports
        // false right after the swap; without this the place polygons vanish
        // on the new basemap. Draw already survives via its persistent control.
        this.applyMode({ force: true });
        if (this.mode === 'draw') {
          this.emitDrawState();
        }
        // Restore the user's pre-swap view instantly (no animation) AFTER the
        // re-add's fit, so the basemap change does not move the camera.
        this.map.jumpTo(camera);
      });
    },
    snapshotFromDraw() {
      // Capture the entire current drawing as a plain, non-reactive
      // FeatureCollection. Deep-cloning prevents handing a live MapboxDraw proxy
      // to Vue's reactivity system (which recurses infinitely on it).
      return cloneJSON(this.draw.getAll());
    },
    recordHistory() {
      // Snapshot the whole drawing after a user change, delegating the stack
      // transition (redo-tail truncation + 50-entry cap) to the pure reducer.
      if (this.suppressHistory) return;
      const next = historyRecordSnapshot(
        { history: this.history, index: this.historyIndex },
        this.snapshotFromDraw(),
        MAX_HISTORY,
      );
      this.history = next.history;
      this.historyIndex = next.index;
    },
    applySnapshot(snapshot) {
      // Replace the entire drawing with the given FeatureCollection snapshot,
      // without recording a new history entry. This method never mutates
      // history/historyIndex itself; on failure it surfaces the error via
      // drawError and re-throws so callers (onUndo/onRedo/onCancel) can revert
      // the index they moved.
      if (!this.draw) return;
      this.suppressHistory = true;
      try {
        this.draw.deleteAll();
        if (snapshot == null) {
          return;
        }
        const fc = cloneJSON(snapshot);
        if (fc && Array.isArray(fc.features) && fc.features.length) {
          fc.features.forEach((feature) => this.draw.add(feature));
        }
        this.draw.changeMode('simple_select');
      } catch (error) {
        this.drawError = 'Could not restore the drawing.';
        throw error;
      } finally {
        this.suppressHistory = false;
      }
    },
    resetHistory() {
      const cleared = historyReset();
      this.history = cleared.history;
      this.historyIndex = cleared.index;
    },
    onDrawModeChange(e) {
      // Only track Drawing_Mode for the draw context; review-edit manages its
      // own selection modes and must not be affected by this handler.
      if (this.mode !== 'draw') return;

      const nextMode = e && e.mode;
      const wasDrawing = this.isDrawing;
      this.isDrawing = nextMode === 'draw_polygon';

      // A transition AWAY from draw_polygon that was NOT preceded by a
      // draw.create means MapboxDraw discarded an in-progress polygon with
      // fewer than 3 distinct vertices. Surface a transient invalid indication
      // (Requirement 1.6). No history is recorded because no create ran.
      if (wasDrawing && nextMode !== 'draw_polygon' && !this.createJustHappened) {
        this.drawError = 'A polygon needs at least 3 points. Drawing was discarded.';
      }
      // Reset the create-tracking flag once the mode transition is observed.
      this.createJustHappened = false;

      this.emitDrawState();
    },
    onDrawCreate(e) {
      // A new polygon with >=3 vertices was completed. Exit Drawing_Mode and
      // record the resulting snapshot so the add is undoable.
      this.isDrawing = false;
      // Mark that a create happened so the subsequent draw.modechange (back to
      // simple_select) is not misread as a discarded sub-3-vertex polygon.
      this.createJustHappened = true;
      this.onDrawUpdate();
      this.recordHistory();

      // IMPORTANT: do NOT call draw.changeMode() synchronously here. draw.create
      // fires from inside Draw's onStop/fire cycle; re-entering synchronously
      // causes infinite recursion in Map.fire/JSON.stringify. Defer it.
      const id = e && e.features && e.features[0] && e.features[0].id;
      if (this.draw && id != null) {
        this.$nextTick(() => {
          if (this.draw) {
            try {
              this.draw.changeMode('direct_select', { featureId: id });
            } catch (error) {
              // If the feature is no longer selectable, ignore.
            }
          }
        });
      }
    },
    onDrawUpdateOrDelete() {
      // Vertex edits (draw.update) and shape deletions (draw.delete) refresh the
      // snapshot and are recorded so they are undoable (Requirement 2.2).
      this.onDrawUpdate();
      this.recordHistory();
    },
    onAddPolygon() {
      // Enter Drawing_Mode from the overlay toolbar without removing existing
      // shapes (mirrors the 'start' draw-command action).
      if (!this.map || this.mode !== 'draw' || !this.draw) return;
      this.draw.changeMode('draw_polygon');
    },
    onUndo() {
      // Move one step back in the History_Stack and restore that snapshot.
      // The reducer computes the target index; applySnapshot performs the
      // (non-recording) MapboxDraw restore. On failure applySnapshot re-throws,
      // so we revert the index and surface an undo-specific error (Req 2.9).
      if (!this.canUndo) return;
      const next = historyUndo({ history: this.history, index: this.historyIndex });
      const previousIndex = this.historyIndex;
      this.historyIndex = next.index;
      try {
        this.applySnapshot(this.currentSnapshot);
        // Refresh drawFeaturesSnapshot so the tab reflects the restored shapes.
        this.onDrawUpdate();
      } catch (error) {
        this.historyIndex = previousIndex;
        this.drawError = 'Could not undo the last change.';
      }
    },
    onRedo() {
      // Move one step forward in the History_Stack and restore that snapshot.
      // Mirrors onUndo: reducer picks the index, applySnapshot restores, and a
      // failure reverts the index with a redo-specific error (Req 3.1, 3.2).
      if (!this.canRedo) return;
      const next = historyRedo({ history: this.history, index: this.historyIndex });
      const previousIndex = this.historyIndex;
      this.historyIndex = next.index;
      try {
        this.applySnapshot(this.currentSnapshot);
        this.onDrawUpdate();
      } catch (error) {
        this.historyIndex = previousIndex;
        this.drawError = 'Could not redo the change.';
      }
    },
    onCancelDrawing() {
      // Discard the in-progress polygon by restoring the current snapshot; any
      // previously finished polygons remain intact. No history entry is recorded
      // for a cancellation. On restore failure keep the finished shapes and exit
      // Drawing_Mode anyway, surfacing a cancel-specific error (Req 4.3-4.6).
      try {
        this.applySnapshot(this.currentSnapshot);
      } catch (error) {
        this.drawError = 'Could not cancel drawing.';
      } finally {
        this.isDrawing = false;
      }
      // Refresh drawFeaturesSnapshot so the tab reflects the restored shapes.
      this.onDrawUpdate();
    },
    async onDeleteDrawing() {
      // The Delete control clears the ENTIRE drawing (every shape) after user
      // confirmation, resetting the History_Stack just like the 'clear' command.
      // A dismissed dialog or a failed clear changes nothing (Req 5.3-5.10, 9.8).
      if (!this.draw) return;

      const totalCount = this.drawFeaturesSnapshot.length;
      const message = buildDeleteMessage(0, totalCount);

      let confirmed;
      try {
        confirmed = await this.$bvModal.msgBoxConfirm(message, {
          title: 'Clear drawing',
          okTitle: 'Clear',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
          // The place-selection modal forces an extreme z-index, so the default
          // message-box modal would render behind it (invisible/unclickable).
          // modalClass lifts our dialog above it (see the non-scoped style).
          modalClass: 'ps-confirm-modal',
        });
      } catch (error) {
        this.drawError = 'Could not open the clear confirmation.';
        return;
      }

      // Dismissing the dialog leaves every shape unchanged and records nothing.
      if (!confirmed) return;

      try {
        this.suppressHistory = true;
        try {
          this.draw.deleteAll();
          this.draw.changeMode('simple_select');
        } finally {
          this.suppressHistory = false;
        }
        this.isDrawing = false;
        this.onDrawUpdate();
        // Clearing the drawing also resets undo/redo history (Req 9.8).
        this.resetHistory();
      } catch (error) {
        // Clear failed after confirm: keep existing shapes, record no history
        // entry, surface a clear-specific error (Req 5.10).
        this.drawError = 'Could not clear the drawing.';
      }
    },
    handleDrawCommand() {
      if (!this.map || this.mode !== 'draw' || !this.drawCommand || !this.drawCommand.action) return;

      const { action, payload } = this.drawCommand;

      if (!this.draw) return;

      switch (action) {
        case 'start':
          // Enter Drawing_Mode without removing existing shapes (Req 1.1, 1.2, 8.5).
          // The user is now drawing, so dismiss any pending "Draw a polygon."
          // warning immediately.
          this.hideDrawWarning();
          this.draw.changeMode('draw_polygon');
          break;
        case 'clear':
          // Clear all shapes and also reset the History_Stack (Req 8.5, 9.8).
          // A clear follows a successful add (or an explicit reset), so any
          // lingering/fading warning must be cancelled to avoid a flash.
          this.hideDrawWarning();
          this.draw.deleteAll();
          this.onDrawUpdate();
          this.resetHistory();
          break;
        case 'remove_shape':
          if (payload && payload.id) {
            this.draw.delete(payload.id);
            this.onDrawUpdate();
          }
          break;
        case 'toggle_style':
          // Recognized action (Req 8.1); style switching is handled by the
          // Basemaps control via changeMapStyle(), so this is a no-op
          // passthrough that leaves shapes/mode/history unchanged (Req 8.5).
          break;
        case 'undo':
          this.onUndo();
          break;
        case 'redo':
          this.onRedo();
          break;
        case 'cancel':
          this.onCancelDrawing();
          break;
        case 'delete':
          // onDeleteDrawing is async (confirmation dialog); fire without
          // awaiting. It emits state itself once the deletion completes.
          this.onDeleteDrawing();
          break;
        case 'warn':
          // Surface the transient "Draw a polygon." prompt because the user
          // tried to add a place without drawing one. It fades on its own and
          // leaves shapes/mode/history unchanged.
          this.showDrawWarning();
          break;
        default:
          // Unrecognized action: leave shapes/mode/history unchanged, flag the
          // error naming the action, and warn (Req 8.2).
          this.drawError = 'Unrecognized draw command: ' + action;
          console.warn('Unrecognized draw command:', action);
          break;
      }

      // Every recognized branch (and the error default) emits the current
      // state so the tab stays in sync (Req 8.3). undo/redo/cancel/delete also
      // emit internally via onDrawUpdate; a redundant emit here is harmless.
      this.emitDrawState();
    },
    showDrawWarning() {
      // Only prompt when there is genuinely no shape drawn. This guards against
      // a stale 'warn' command arriving right as a place is added/cleared,
      // which previously caused the banner to flash.
      if (this.drawFeaturesSnapshot.length > 0) return;

      // Show the "Draw a polygon." warning banner, then fade it out after a
      // short delay. Re-triggering restarts the timer so the message stays
      // visible for the full duration on each attempt.
      this.hideDrawWarning();
      this.showPolygonWarning = true;
      this.polygonWarningTimer = setTimeout(() => {
        this.showPolygonWarning = false;
        this.polygonWarningTimer = null;
      }, 3000);
    },
    hideDrawWarning() {
      // Cancel any pending fade timer and hide the warning immediately.
      if (this.polygonWarningTimer) {
        clearTimeout(this.polygonWarningTimer);
        this.polygonWarningTimer = null;
      }
      this.showPolygonWarning = false;
    },
    getReviewEditPlace() {
      if (!this.reviewEditPlaceId) return null;
      return this.selectedPlaces.find((place) => place && place._id === this.reviewEditPlaceId) || null;
    },
    ensureReviewEditMode() {
      if (!this.map || !this.reviewEditPlaceId) return;

      const place = this.getReviewEditPlace();
      if (!place || !place.geometry || !this.draw) return;

      if (this.activeReviewEditPlaceId === this.reviewEditPlaceId) return;

      const feature = {
        id: this.reviewEditPlaceId,
        type: 'Feature',
        properties: {
          placeId: this.reviewEditPlaceId,
        },
        geometry: place.geometry,
      };

      this.draw.deleteAll();
      this.draw.add(feature);
      this.draw.changeMode('simple_select', { featureIds: [this.reviewEditPlaceId] });
      this.activeReviewEditPlaceId = this.reviewEditPlaceId;
      this.fitFeatures([feature], 80, 16);
    },
    getReviewEditGeometry() {
      if (!this.draw || !this.drawControlAdded) return null;
      try {
        const all = this.draw.getAll();
        const feature = all && Array.isArray(all.features) ? all.features[0] : null;
        return feature && feature.geometry ? feature.geometry : null;
      } catch (error) {
        return null;
      }
    },
    emitReviewEditSaved() {
      this.$emit('review-edit-saved', {
        id: this.reviewEditPlaceId,
        geometry: this.getReviewEditGeometry(),
      });
    },
    handleReviewEditCommand() {
      if (!this.map || this.mode !== 'review-edit' || !this.reviewEditCommand || !this.reviewEditCommand.action) return;

      const { action } = this.reviewEditCommand;

      if (action === 'start') {
        this.ensureReviewEditMode();
        return;
      }

      if (action === 'save') {
        this.emitReviewEditSaved();
        return;
      }

      if (action === 'cancel') {
        this.clearDrawTools();
      }
    },
    handleReviewFocusCommand() {
      // Zoom the map to a single reviewed place so the user can inspect its
      // location and polygon. Only meaningful while reviewing (not editing).
      if (!this.map || this.mode !== 'review' || !this.reviewFocusCommand || !this.reviewFocusCommand.id) return;

      const place = this.selectedPlaces.find(
        (p) => p && p._id === this.reviewFocusCommand.id,
      );
      if (!place) return;

      // Places carry a top-level geometry (Point/Polygon/MultiPolygon); CSV
      // uploads instead carry a set of preview Point features.
      if (place.geometry) {
        this.fitFeatures(
          [{ type: 'Feature', properties: { _id: place._id }, geometry: place.geometry }],
          80,
          16,
        );
        return;
      }

      if (Array.isArray(place.previewFeatures) && place.previewFeatures.length) {
        this.fitFeatures(place.previewFeatures, 60, 15);
      }
    },
    updateUploadData() {
      console.log('Updating upload data on map with features:', this.uploadPreviewFeatures);
      console.log('Current map state:', this.map, this.map?.isStyleLoaded());
      if (!this.map) return;

      const sourceId = 'ps-shared-upload-preview';
      const src = this.map.getSource(sourceId);
      console.log('Got map source for upload preview:', src);
      if (!src) return;

      src.setData({
        type: 'FeatureCollection',
        features: this.uploadPreviewFeatures,
      });
      console.log('Set upload preview data on map source');

      this.renderUploadMarkers();

      if (!this.uploadPreviewFeatures.length) return;
      this.fitFeatures(this.uploadPreviewFeatures, 40, 13);
    },
    updateReviewData({ skipFit = false } = {}) {
      if (!this.map) return;

      this.updateReviewPolygons();

      this.renderReviewMarkers();
      if (!skipFit) {
        this.fitReviewBounds();
      }
    },
    updateReviewPolygons() {
      // Populate the per-source polygon layers. In active-only mode (Builder_Map)
      // each layer is fed ONLY the active place's polygon (or emptied when there
      // is no active place, or when the active place has no/invalid geometry).
      // Existing callers (e.g. the modal review step) never pass activePlaceId,
      // so every place's polygon keeps rendering as before.
      if (!this.map) return;

      const editingId = this.mode === 'review-edit' ? this.reviewEditPlaceId : null;
      const activeOnly = this.isActiveOnlyMode();
      const activeId = this.activePlaceId != null ? String(this.activePlaceId) : null;

      for (const source of Object.keys(SOURCE_COLORS)) {
        const sourceId = `ps-shared-${source}`;
        const src = this.map.getSource(sourceId);
        if (!src) continue;

        let features;
        if (activeOnly) {
          features = this.selectedPlaces
            .filter(
              (p) => p
                && p.geometry
                && activeId !== null
                && String(p._id) === activeId
                && (p._source || 'library') === source,
            )
            .map((p) => ({
              type: 'Feature',
              properties: { name: p.name, _id: p._id },
              geometry: p.geometry,
            }));
        } else {
          features = this.selectedPlaces
            .filter((p) => p.geometry && (p._source || 'library') === source && p._id !== editingId)
            .map((p) => ({
              type: 'Feature',
              properties: { name: p.name, _id: p._id },
              geometry: p.geometry,
            }));
        }

        src.setData({ type: 'FeatureCollection', features });
      }
    },
    isActiveOnlyMode() {
      // The builder passes activePlaceId explicitly (even when null) to opt into
      // active-only polygon rendering; other callers never pass the prop.
      const propsData = this.$options.propsData || {};
      return Object.prototype.hasOwnProperty.call(propsData, 'activePlaceId');
    },
    fitReviewBounds() {
      if (!this.map || !this.allFeatures.length) return;
      this.fitFeatures(this.allFeatures, 40, 14);
    },
    fitFeatures(features, padding = 40, maxZoom = 14) {
      if (!this.map || !Array.isArray(features) || !features.length) return;

      try {
        const bounds = new mapboxgl.LngLatBounds();
        features.forEach((f) => {
          const coords = this.extractCoords(f.geometry);
          coords.forEach((c) => bounds.extend(c));
        });

        if (!bounds.isEmpty()) {
          this.map.fitBounds(bounds, { padding, maxZoom });
        }
      } catch (error) {
        console.warn('PlaceSelectionSharedMap fitFeatures error', error);
      }
    },
    extractCoords(geometry) {
      if (!geometry) return [];
      const flat = (arr) => (Array.isArray(arr[0]) ? arr.flatMap(flat) : [arr]);

      if (geometry.type === 'Point') return [geometry.coordinates];
      if (geometry.type === 'Polygon') return flat(geometry.coordinates);
      if (geometry.type === 'MultiPolygon') return geometry.coordinates.flatMap((poly) => flat(poly));
      return [];
    },
    applyMode({ force = false } = {}) {
      if (!this.map) return;
      // `force` is passed from the post-setStyle `style.load` handler in
      // changeMapStyle, where map.isStyleLoaded() can momentarily report false
      // right after a basemap swap even though the style is effectively ready.
      // Skipping the gate there is safe because every layer add below is
      // idempotent (guarded by getSource/getLayer existence checks) and the
      // data setters no-op when their source is absent, so a forced re-add
      // cannot double-add. All other callers keep the gated behavior.
      if (!force && !this.map.isStyleLoaded()) return;

      this.mountGeocoder();
      this.clearReviewMarkers();
      this.clearReviewLayers();
      this.clearReviewEditHighlightLayers();
      this.clearUploadLayers();
      this.clearExploreLayers();

      if (this.mode === 'review') {
        this.clearDrawTools();
        this.ensureReviewLayers();
        this.updateReviewData({ skipFit: this.skipNextReviewFit });
        this.skipNextReviewFit = false;
        return;
      }

      if (this.mode === 'upload') {
        this.clearDrawTools();
        this.ensureUploadLayers();
        this.updateUploadData();
        return;
      }

      if (this.mode === 'draw') {
        this.ensureDrawTools('draw');
        this.handleDrawCommand();
        return;
      }

      if (this.mode === 'review-edit') {
        this.ensureReviewLayers();
        this.updateReviewData({ skipFit: true });
        this.ensureReviewEditHighlightLayers();
        this.updateReviewEditHighlightData();
        this.ensureDrawTools('review-edit');
        this.ensureReviewEditMode();
        this.handleReviewEditCommand();
        return;
      }

      if (this.mode === 'select') {
        this.clearDrawTools();
        this.ensureExploreLayers();
        this.updateExploreData();
        return;
      }

      this.clearDrawTools();
    },
  },
};
</script>

<style lang="scss" scoped>
.place-selection-shared-map {
  position: relative;
  width: 100%;
  height: 100%;
}

.draw-map-geocoder-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 6;
  pointer-events: auto;

  ::v-deep mapbox-search-box {
    display: block;
    max-width: 60vw;
    width: 360px;
  }
}

/* Mapbox navigation (zoom) controls sit top-left, pushed below the geocoder. */
:deep(.mapboxgl-ctrl-top-left) {
  top: 32px;
  left: -16px;
}

.place-selection-shared-map__container {
  width: 100%;
  height: 100%;
  min-height: 400px;
  border-radius: 6px;
}

/* Contextual banner hints overlaid on the map, tucked under the draw toolbar
   in the top-right corner (mirrors PlaceEditModal .pem-map-banner). */
.ps-map-banner {
  position: absolute;
  top: 60px;
  right: 12px;
  max-width: 320px;
  padding: 0.35rem 0.6rem;
  border-radius: 0.35rem;
  font-size: 0.78rem;
  text-align: center;
  z-index: 7;
  pointer-events: none;
}

.ps-map-banner--info {
  background: rgba(18, 37, 75, 0.82);
  color: #ffffff;
}

.ps-map-banner--warning {
  background: rgba(220, 53, 69, 0.9);
  color: #ffffff;
}

/* Fade the transient "Draw a polygon." warning in and out. */
.ps-map-banner-fade-enter-active,
.ps-map-banner-fade-leave-active {
  transition: opacity 0.5s ease;
}

.ps-map-banner-fade-enter,
.ps-map-banner-fade-leave-to {
  opacity: 0;
}

/* Draw controls overlaid on the map (top-right), replacing the old
   in-panel toolbar. The contextual banner sits just below this. */
.ps-draw-toolbar {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 8;
  display: flex;
  gap: 0.5rem;
  padding: 0.4rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  pointer-events: auto;
}

.ps-draw-toolbar__btn {
  white-space: nowrap;
}

:deep(.upload-center-marker) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

:deep(.upload-center-marker__dot) {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #4f14dc;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.5);
}

/* Highlighted state for the Active_Place's marker (Builder_Map). Toggled via
   the activePlaceId watch without re-rendering the marker. */
:deep(.upload-center-marker.is-active) {
  z-index: 2;
}

:deep(.upload-center-marker.is-active .upload-center-marker__dot) {
  width: 24px;
  height: 24px;
  background: #f59e0b;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.35), 0 4px 4px 0 rgba(0, 0, 0, 0.5);
}

:deep(.upload-center-marker__label) {
  margin: 4px 0 0;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
  color: #111827;
  white-space: nowrap;
  text-shadow: -1px -1px 0 #ffffff, 1px -1px 0 #ffffff, -1px 1px 0 #ffffff, 1px 1px 0 #ffffff;
}
</style>

<!-- Non-scoped: the clear-drawing confirmation is a BootstrapVue message-box
     modal appended to <body>, so it lives outside this component's scoped tree.
     The place-selection modal forces z-index 2147482999, so the confirm dialog
     must sit above that to be visible and clickable. We lift only our own
     dialog (via modalClass) rather than the global .modal-backdrop, to avoid
     affecting unrelated modals elsewhere in the app. -->
<style>
div[id$="___BV_modal_outer_"]:has(.modal.ps-confirm-modal) {
  z-index: 2147483001 !important;
}
</style>